//import Vapi from "./vapi";
import Vapi from "@vapi-ai/web";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import en from "src/localization/english/en";
import { getAccessToken } from "src/utils/authentication.service";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;

const VapiContext = React.createContext<any>(null);

interface VapiContextProviderProps {
  children: React.ReactNode;
}

export const VapiContextProvider: React.FC<VapiContextProviderProps> = ({ children }) => {
  const context = useState({
    vapiClient: null,
    started: false,
    ended: false,
    transcripts: [],
    videoReady: false,   
    isVideoCall: false, 
  });
  return (
    <VapiContext.Provider value={context}>
      {children}
    </VapiContext.Provider>
  );
};

export const useVapi = () => {
  const [context, setContext] = useContext(VapiContext);
  
  useEffect(() => {
    if(context.vapiClient) {
      return;
    }

    //const vapiClient = new Vapi();
    const vapiClient = new Vapi(getAccessToken(), `${apiUrl}/api/1.0/simulations/vapi`);
    //const vapiClient = new Vapi("4ae9f112-d64f-4788-ae5d-ef2f1eb38ac7");
    vapiClient?.on("message", handleMessageChange);

    vapiClient?.on("call-start", handleCallStart);

    vapiClient?.on("call-end", handleCallEnd);

    vapiClient?.on("volume-level", handleVolumeChange);

    setContext((prev) => {
      return {
        ...prev,
        vapiClient: vapiClient,
        transcripts: [],
      }
    });

    return () => {
      if (vapiClient) {
        vapiClient?.off("message", handleMessageChange);
        vapiClient?.off("call-start", handleCallStart);
        vapiClient?.off("call-end", handleCallEnd);
        vapiClient?.off("volume-level", handleVolumeChange);
      }
    }
  }, []);
  
  const startCall = async (sessionId: string, isVideo = false) => {
    try {
      if (!context.vapiClient) return;
  
      if (isVideo) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 3840 },     
            height: { ideal: 2160 },    
            frameRate: { ideal: 60 }, 
            facingMode: 'user',
            aspectRatio: { ideal: 16/9 },
          }
        });
        setContext((prev: any) => ({ 
          ...prev, 
          videoStream: stream,
          isVideoCall: true,   
        }));
      }
  
      await context.vapiClient.start(sessionId);
    } catch (err) {
      console.error("Failed to start call:", err);
    }
  };

const handleCallStart = () => {
  setContext((prev: any) => {
    if (prev.isVideoCall && prev.vapiClient) {
      prev.vapiClient.setMuted(true);
    }

    return {
      ...prev,
      started: true,
      ended: false,
      videoReady: !prev.isVideoCall,
    };
  });
};

  const handleCallEnd = () => {
    setContext((prev: any) => {
      prev.videoStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      return {
        ...prev,
        started: false,
        ended: true,
        videoStream: null,
      }
    });
  }

  const handleVolumeChange = (volume: number) => {
    setContext((prev: any) => {
      return {
        ...prev,
        volume: volume,
      }
    });
  }

  const handleMessageChange = (message: any) => {
    if (message.type === 'conversation-update') {
      const transcripts = (message.conversation || []).filter((conversation: any) => {
        return _.includes(['assistant', 'user'], conversation.role);
      });

      setContext((prev: any) => {
        return {
          ...prev,
          transcripts: transcripts
        }
      });
    }
  }

  const setVideoReady = (ready: boolean) => {
    setContext((prev: any) => {
      if (ready) prev.vapiClient?.setMuted(false);
      return { ...prev, videoReady: ready };
    });
  };

  return { ...context, startCall, setVideoReady };
}